/** Capture de la vue courante pour une anomalie signalée (registre F39), sans dépendance : la page est recopiée
 *  dans une image SVG (`foreignObject`) avec ses feuilles de style et sa police, puis dessinée sur un canevas et
 *  exportée en JPEG. La carte, dessinée en WebGL, est relue dans l'image qu'elle vient de rendre (`setCaptureMap`,
 *  `render` de Mapbox : le tampon est encore lisible) ; les autres canevas par `toDataURL`.
 *
 *  La copie est prise au moment de l'appel, avant l'ouverture de la fenêtre du retour : la fenêtre n'y figure jamais,
 *  ni les menus (`[role="menu"]`) ni ce qui porte `data-capture-skip`. Limites connues : positions de défilement
 *  intérieures remises au début, images d'une autre origine absentes, Safari qui refuse parfois de relire le
 *  canevas — la capture est alors indisponible (`null`) et l'utilisateur peut joindre une image à la place. */

export interface MapSource {
  getCanvas(): HTMLCanvasElement;
  once(type: "render", listener: () => void): unknown;
  triggerRepaint(): void;
}

let mapSource: MapSource | null = null;

/** Carte de l'application, posée par `MapCanvas` (lecture de son image pour la capture seulement). */
export function setCaptureMap(map: MapSource | null): void {
  mapSource = map;
}

/** Plus grand côté de l'image envoyée, et qualité JPEG : environ 150 à 400 Ko pour un écran d'ordinateur. */
export const CAPTURE_MAX_SIDE = 1600;
export const CAPTURE_QUALITY = 0.78;
const MAP_WAIT_MS = 700;
const FONT_BUDGET = 700 * 1024;

/** Image de la carte (JPEG en `data:`), lue pendant son prochain rendu ; null sans carte ou après 700 ms. */
function mapImage(): Promise<{ canvas: HTMLCanvasElement; url: string } | null> {
  const map = mapSource;
  if (!map) return Promise.resolve(null);
  return new Promise((resolve) => {
    let done = false;
    const finish = (value: { canvas: HTMLCanvasElement; url: string } | null) => {
      if (done) return;
      done = true;
      resolve(value);
    };
    const timer = setTimeout(() => finish(null), MAP_WAIT_MS);
    try {
      map.once("render", () => {
        clearTimeout(timer);
        try {
          const canvas = map.getCanvas();
          finish({ canvas, url: canvas.toDataURL("image/jpeg", 0.85) });
        } catch {
          finish(null);
        }
      });
      map.triggerRepaint();
    } catch {
      clearTimeout(timer);
      finish(null);
    }
  });
}

/** Texte des feuilles de style de la page (celles d'une autre origine, illisibles, sont passées). */
function styleText(doc: Document): string {
  const parts: string[] = [];
  for (const sheet of Array.from(doc.styleSheets)) {
    try {
      parts.push(Array.from(sheet.cssRules, (rule) => rule.cssText).join("\n"));
    } catch {
      // Feuille d'une autre origine : ignorée.
    }
  }
  return parts.join("\n");
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error("lecture"));
    reader.readAsDataURL(blob);
  });
}

const fontCache = new Map<string, string>();

/** Règles `@font-face` de la police de l'interface, fichiers incorporés (une image SVG ne charge rien). */
async function fontFaces(doc: Document): Promise<string> {
  const family = (getComputedStyle(doc.body).fontFamily.split(",")[0] ?? "").replace(/["']/g, "").trim();
  if (!family) return "";
  const out: string[] = [];
  let budget = FONT_BUDGET;
  for (const sheet of Array.from(doc.styleSheets)) {
    let rules: CSSRule[] = [];
    try {
      rules = Array.from(sheet.cssRules);
    } catch {
      continue;
    }
    for (const rule of rules) {
      if (!(rule instanceof CSSFontFaceRule)) continue;
      if (rule.style.getPropertyValue("font-family").replace(/["']/g, "").trim() !== family) continue;
      let text = rule.cssText;
      const urls = Array.from(text.matchAll(/url\(["']?([^"')]+)["']?\)/g), (m) => m[1]!);
      for (const raw of urls) {
        if (raw.startsWith("data:")) continue;
        const absolute = new URL(raw, sheet.href ?? doc.baseURI).href;
        let data = fontCache.get(absolute);
        if (!data) {
          try {
            const res = await fetch(absolute);
            const blob = await res.blob();
            if (!res.ok || blob.size > budget) continue;
            budget -= blob.size;
            data = await blobToDataUrl(blob);
            fontCache.set(absolute, data);
          } catch {
            continue;
          }
        }
        text = text.split(raw).join(data);
      }
      if (!/url\((?!["']?data:)/.test(text)) out.push(text);
    }
  }
  return out.join("\n");
}

/** Copie du document à l'instant de l'appel : valeurs saisies reportées, canevas remplacés par leur image, menus
 *  et éléments `data-capture-skip` retirés. */
function snapshot(doc: Document, mapCanvas: HTMLCanvasElement | null): HTMLElement {
  const source = doc.body;
  const copy = source.cloneNode(true) as HTMLElement;
  const originals = source.querySelectorAll("*");
  const copies = copy.querySelectorAll("*");
  const drop: Element[] = [];
  originals.forEach((original, index) => {
    const twin = copies[index] as HTMLElement | undefined;
    if (!twin) return;
    if (original.matches('[role="menu"], [data-capture-skip], script, noscript')) {
      drop.push(twin);
      return;
    }
    if (original instanceof HTMLInputElement) {
      if (original.type === "password") twin.setAttribute("value", "");
      else twin.setAttribute("value", original.value);
      if (original.checked) twin.setAttribute("checked", "");
    } else if (original instanceof HTMLTextAreaElement) {
      twin.textContent = original.value;
    } else if (original instanceof HTMLCanvasElement) {
      // La carte : image posée ensuite, lue pendant son rendu (`mapImage`).
      let url: string | null = null;
      if (original !== mapCanvas) {
        try {
          url = original.toDataURL("image/png");
        } catch {
          url = null;
        }
      }
      const img = doc.createElement("img");
      if (original === mapCanvas) img.setAttribute("data-capture-map", "");
      const rect = original.getBoundingClientRect();
      img.setAttribute("style", original.getAttribute("style") ?? "");
      img.style.width = `${rect.width}px`;
      img.style.height = `${rect.height}px`;
      img.className = original.className;
      if (url) img.setAttribute("src", url);
      twin.replaceWith(img);
    } else if (original instanceof HTMLImageElement && original.currentSrc && !original.currentSrc.startsWith("data:")) {
      try {
        const canvas = doc.createElement("canvas");
        canvas.width = original.naturalWidth;
        canvas.height = original.naturalHeight;
        canvas.getContext("2d")?.drawImage(original, 0, 0);
        twin.setAttribute("src", canvas.toDataURL("image/png"));
      } catch {
        twin.removeAttribute("src");
      }
    }
  });
  for (const element of drop) element.remove();
  return copy;
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("image"));
    image.src = url;
  });
}

/** Taille du canevas de sortie : la vue à l'échelle 1, réduite pour que le plus grand côté tienne en `max`. */
export function outputSize(width: number, height: number, max = CAPTURE_MAX_SIDE): { width: number; height: number } {
  const scale = Math.min(1, max / Math.max(width, height, 1));
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
}

/** Démarre la capture de la vue telle qu'elle est maintenant (copie synchrone du document, image de la carte au
 *  rendu suivant) ; la promesse rend l'image JPEG en `data:`, ou null si le navigateur ne la permet pas. */
export function captureView(doc: Document = document): Promise<string | null> {
  if (typeof window === "undefined" || !doc.body) return Promise.resolve(null);
  const width = window.innerWidth;
  const height = window.innerHeight;
  const mapCanvas = mapSource?.getCanvas() ?? null;
  const map = mapImage();
  // Copie prise tout de suite, carte posée ensuite : la fenêtre du retour, ouverte juste après, n'y est pas.
  const early = snapshot(doc, mapCanvas);
  return (async () => {
    try {
      const [mapShot, fonts] = await Promise.all([map, fontFaces(doc)]);
      // Image de la carte posée à la place de son canevas dans la copie déjà prise.
      if (mapShot) early.querySelector("[data-capture-map]")?.setAttribute("src", mapShot.url);
      const html = doc.documentElement;
      const attributes = Array.from(html.attributes)
        .map((a) => `${a.name}="${a.value.replace(/&/g, "&amp;").replace(/"/g, "&quot;")}"`)
        .join(" ");
      const body = new XMLSerializer().serializeToString(early);
      // Image figée : une animation y resterait à sa première image (panneaux et libellés en fondu invisibles).
      // ui-check: ignore-start — feuille de la seule image de capture, jamais appliquée à l'interface.
      const frozen = "*,*::before,*::after{animation:none !important;transition:none !important}";
      // ui-check: ignore-end
      const css = `${fonts}\n${styleText(doc)}\n${frozen}`;
      const svg =
        `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}">` +
        `<foreignObject x="0" y="0" width="100%" height="100%">` +
        `<html xmlns="http://www.w3.org/1999/xhtml" ${attributes}><head><style>${css.replace(/&/g, "&amp;").replace(/</g, "&lt;")}</style></head>${body}</html>` +
        `</foreignObject></svg>`;
      const image = await loadImage(`data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`);
      const size = outputSize(width, height);
      const canvas = doc.createElement("canvas");
      canvas.width = size.width;
      canvas.height = size.height;
      const context = canvas.getContext("2d");
      if (!context) return null;
      const background = getComputedStyle(doc.body).backgroundColor;
      context.fillStyle = background && background !== "rgba(0, 0, 0, 0)" ? background : "white";
      context.fillRect(0, 0, size.width, size.height);
      context.drawImage(image, 0, 0, size.width, size.height);
      return canvas.toDataURL("image/jpeg", CAPTURE_QUALITY);
    } catch {
      return null;
    }
  })();
}

/** Image choisie par l'utilisateur (PNG, JPEG ou WebP, 8 Mo au plus) ramenée en JPEG de 1600 px au plus ; null si
 *  elle n'est pas lisible. */
export async function imageFileToDataUrl(file: File): Promise<string | null> {
  if (!/^image\/(png|jpeg|webp)$/.test(file.type) || file.size > 8 * 1024 * 1024) return null;
  try {
    const url = await blobToDataUrl(file);
    const image = await loadImage(url);
    const size = outputSize(image.naturalWidth, image.naturalHeight);
    const canvas = document.createElement("canvas");
    canvas.width = size.width;
    canvas.height = size.height;
    const context = canvas.getContext("2d");
    if (!context) return null;
    context.fillStyle = "white";
    context.fillRect(0, 0, size.width, size.height);
    context.drawImage(image, 0, 0, size.width, size.height);
    return canvas.toDataURL("image/jpeg", CAPTURE_QUALITY);
  } catch {
    return null;
  }
}
